import React from 'react';
import { Box, Typography } from '@mui/material';

// ── תפילה אחרי ספירה — Parchment ────────────────────────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 2, textAlign: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, justifyContent: 'center' }}>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, #c8a84b)' }} />
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.6rem',
        color: '#8b6914',
        letterSpacing: '0.12em',
        whiteSpace: 'nowrap',
      }}>
        ✦ {section.title} ✦
      </Typography>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #c8a84b, transparent)' }} />
    </Box>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.67rem',
        color: '#3b2000',
        lineHeight: 2,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

// ── תבנית קלף ────────────────────────────────────────────────────────────────
const Parchment = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText, omerDayHebrew, sefirahName,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showPasuk, pasukText,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  const bg = bgColor || '#fdf3d0';

  const Divider = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.25, width: '100%', maxWidth: 400 }}>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, #c8a84b)' }} />
      <Typography sx={{ color: '#c8a84b', fontSize: '0.7rem' }}>✦</Typography>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #c8a84b, transparent)' }} />
    </Box>
  );

  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bg,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
      }}>
        <Typography sx={{ color: '#8b6914', fontFamily: 'Frank Ruhl Libre, serif', fontSize: '0.9rem', opacity: 0.7 }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '14px 16px',
      direction: 'rtl',
      overflowY: 'auto',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 8,
        border: '2px solid #c8a84b',
        borderRadius: 1,
        pointerEvents: 'none',
        opacity: 0.6,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 11,
        border: '1px solid #c8a84b',
        borderRadius: 0.5,
        pointerEvents: 'none',
        opacity: 0.35,
      },
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(200,168,75,0.35)', borderRadius: 2 },
    }}>
      {/* כותרת */}
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.7rem',
          color: '#8b6914',
          letterSpacing: '0.12em',
          mt: 0.5,
        }}>
          {title}
        </Typography>
      )}

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <>
          <Divider />
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: '#8b6914',
            textAlign: 'center',
            direction: 'rtl',
            opacity: 0.85,
          }}>
            {previewCountdown}
          </Typography>
        </>
      )}

      <Divider />

      {/* לשם ייחוד */}
      {showLeshemYichud && (
        <>
          {leshemYichudLines.map((line, i) => (
            <Typography key={i} sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '0.6rem',
              color: 'rgba(59,32,0,0.7)',
              lineHeight: 1.9,
              textAlign: 'center',
              direction: 'rtl',
              maxWidth: 380,
            }}>
              {line}
            </Typography>
          ))}
          <Divider />
        </>
      )}

      {/* פסוק */}
      {showPasuk !== 'none' && pasukText && (
        <>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.63rem',
            color: '#8b6914',
            lineHeight: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            direction: 'rtl',
            maxWidth: 380,
          }}>
            {pasukText}
          </Typography>
          <Divider />
        </>
      )}

      {/* ברכה */}
      {showBracha && (
        <>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.75rem',
            color: '#3b2000',
            lineHeight: 2.1,
            textAlign: 'center',
            direction: 'rtl',
            maxWidth: 400,
          }}>
            {brachaText}
          </Typography>
          <Divider />
        </>
      )}

      {/* מספר */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(3rem, 11vw, 5.5rem)',
        fontWeight: 700,
        color: '#8b6914',
        lineHeight: 1,
        letterSpacing: '0.05em',
      }}>
        {omerDayHebrew}
      </Typography>

      {/* שורת ספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.9rem, 3vw, 1.3rem)',
        color: '#3b2000',
        mt: 0.75,
        textAlign: 'center',
        direction: 'rtl',
        letterSpacing: '0.03em',
      }}>
        {mainCountText}
      </Typography>

      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.72rem, 2.2vw, 0.95rem)',
          color: '#8b6914',
          mt: 0.4,
          textAlign: 'center',
          direction: 'rtl',
        }}>
          {subCountText}
        </Typography>
      )}

      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.65rem',
        color: 'rgba(139,105,20,0.7)',
        mt: 0.75,
        fontStyle: 'italic',
      }}>
        {sefirahName}
      </Typography>

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Divider />
          {afterCountSections.map((section, i) => (
            <React.Fragment key={section.id}>
              <AfterCountSection section={section} />
              {i < afterCountSections.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      )}

      <Divider />
    </Box>
  );
};

export default Parchment;
